import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Trophy, Instagram, Share2, CheckCircle2, MessageCircle, ExternalLink, Send } from "lucide-react";
import { toast } from "sonner";

const KIND_ICON = {
  instagram_follow: Instagram,
  instagram_community: Instagram,
  whatsapp_community: MessageCircle,
  form: Send,
  link: ExternalLink,
};
const KIND_STYLE = {
  instagram_follow: "bg-magenta text-white",
  instagram_community: "bg-purple text-white",
  whatsapp_community: "bg-lime text-ink",
  form: "bg-skyx text-bg",
  link: "bg-white/10 text-white",
};

export default function ContestsPage() {
  const { user } = useAuth();
  const [contests, setContests] = useState([]);
  const [enteredIds, setEnteredIds] = useState(new Set());

  useEffect(() => {
    document.title = "Contests & Giveaways | Eventom";
    api.get("/contests").then(({ data }) => setContests(data)).catch(() => {});
  }, []);

  const enter = async (id) => {
    if (!user) { toast("Login to enter"); return; }
    try { await api.post(`/contests/${id}/enter`); setEnteredIds((s) => new Set([...s, id])); toast.success("You're in"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const buildCtas = (c) => {
    if (c.ctas?.length) return c.ctas;
    // Backward compatibility for older contests without ctas array
    if (c.mechanic === "instagram_follow") {
      const handle = (c.instagram_handle || "eventom.online").replace(/^@/, "");
      return [{ kind: "instagram_follow", label: `Follow @${handle} on Instagram`, url: `https://instagram.com/${handle}` }];
    }
    if (c.mechanic === "share_event") return [{ kind: "link", label: "Share an event to qualify", url: "/" }];
    return [];
  };

  return (
    <Layout>
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="chip bg-pink/10 text-pink mb-3">Contests & Giveaways</div>
        <h1 className="font-display uppercase text-5xl md:text-6xl leading-[0.9] mb-2">Win the <span className="marker-lime">chaos.</span></h1>
        <p className="text-muted mb-8">Follow, join, share — and win. Tap a CTA below to qualify, then hit Enter Now.</p>

        {contests.length === 0 ? (
          <div className="glass rounded-3xl p-8 text-center font-mono text-sm text-muted">No active contests right now. Follow us on Instagram for drops.</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {contests.map((c) => {
              const ended = c.status !== "active";
              const isEntered = enteredIds.has(c.id);
              const cover = c.cover_url || c.image_path;
              const ctas = buildCtas(c);
              return (
                <div key={c.id} className="glass rounded-3xl overflow-hidden card-lift" data-testid={`contest-${c.id}`}>
                  {cover ? (
                    <div className="aspect-[16/9] overflow-hidden">
                      <img src={cover} alt={c.name} loading="lazy" className="w-full h-full object-cover" data-testid={`contest-cover-${c.id}`} />
                    </div>
                  ) : (
                    <div className="aspect-[16/9] bg-gradient-to-br from-magenta/25 via-purple/25 to-lime/10 flex items-center justify-center"><Trophy className="w-10 h-10 text-white/40" /></div>
                  )}
                  <div className="p-5">
                    <div className="flex items-center justify-between">
                      <Trophy className="w-6 h-6 text-lime" />
                      <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full ${ended ? "bg-white/10 text-muted" : "bg-lime text-ink"}`}>{ended ? "Ended" : "Active"}</span>
                    </div>
                    <div className="font-display text-2xl uppercase leading-tight mt-3">{c.name}</div>
                    <div className="text-sm text-text/80 mt-2">{c.description}</div>
                    <div className="mt-3 text-[11px] font-mono uppercase tracking-widest text-lime">Prize: {c.prize}</div>
                    <div className="text-[10px] font-mono text-muted mt-1">Ends: {c.end_date}</div>

                    {ctas.length > 0 && !ended && (
                      <div className="mt-4 space-y-1.5" data-testid={`contest-ctas-${c.id}`}>
                        {ctas.map((cta, i) => {
                          const Icon = KIND_ICON[cta.kind] || ExternalLink;
                          const style = KIND_STYLE[cta.kind] || "bg-white/10 text-white";
                          return (
                            <a key={i} href={cta.url} target="_blank" rel="noreferrer noopener" data-testid={`contest-cta-${c.id}-${i}`}
                              className={`w-full inline-flex items-center gap-2 justify-center py-2 px-3 rounded-full font-heading font-bold text-[11px] uppercase tracking-widest ${style} hover:opacity-90`}>
                              <Icon className="w-3.5 h-3.5" /> {cta.label}
                            </a>
                          );
                        })}
                      </div>
                    )}

                    {ended && c.winners?.length ? (
                      <div className="mt-3 text-[11px] font-mono text-lime">Winners: {c.winners.map((w) => w.name).join(", ")}</div>
                    ) : null}
                    <button disabled={ended || isEntered} onClick={() => enter(c.id)} data-testid={`contest-enter-${c.id}`} className="btn-primary w-full justify-center mt-4">
                      {isEntered ? <><CheckCircle2 className="w-4 h-4" /> Entered</> : ended ? "Contest Ended" : "Enter Now"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </Layout>
  );
}
